// ─── Toolbar items ──────────────────────────────────────────────────────────

// Declared by `FilterToolbar`, which renders them; a screen reaches them
// through this barrel alongside its column definitions.
import type { ToolbarCount, ToolbarItem } from '../FilterToolbar'
import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import type { SortValue } from './sort'
import type { FilterSchema, ListFilters } from './use-list-filters'
import type { MenuAction } from '@/components/base'
import type { DragMode } from '@/components/SortableItem'

export type ColumnAlign = 'left' | 'center' | 'right'

export type DataTableColumn<T> = {
  key: string
  label: string
  /** A Tailwind width class — `w-40`, `flex-1`, `w-1/3`. */
  width: string
  align?: ColumnAlign
  /**
   * Offers the head control. A column that carries `sortValue` has it already;
   * this is for a table whose order the server decides, where the column has no
   * value to give.
   */
  sortable?: boolean
  /**
   * What the column orders by, where the table sorts its own rows. It sits on
   * the column rather than in a record beside it so that a renamed key takes
   * its value with it — `useTableSort` and `sortByKey` both read it from here.
   */
  sortValue?: (item: T) => SortValue
  render?: (item: T, index: number) => ReactNode
}

export type { ToolbarCount, ToolbarItem }

// ─── Sort ───────────────────────────────────────────────────────────────────

type SortDirection = 'asc' | 'desc'

export type SortState = {
  key: string
  direction: SortDirection
}

// ─── Selection, expansion, tree ─────────────────────────────────────────────

/**
 * Width and spacing of the leading checkbox column under
 * `selectionMode='multi'`.
 *
 * First in the row and outside the tree indent, so every checkbox lines up on
 * the same edge whatever a row's depth — the column is about the row, not
 * about where it sits in the tree.
 */
export const SELECTION_COL_CLASS = 'w-8 shrink-0 pr-2'

/**
 * The chevron sits against the glyph it opens, so the column is only as wide
 * as the caret and rows without children keep the space to stay aligned.
 *
 * `box-content` because the tree's indent is this column's padding: the
 * chevron travels with the label it opens, and the width above stays the
 * caret's own however deep the row sits. The columns after it keep their
 * edges only while the first of them is elastic, which both trees lead with.
 */
export const EXPAND_COL_CLASS = 'box-content w-5 shrink-0 -mr-1'

/** The grip hangs into the row's own padding, so the column costs the cells
 *  the width of the glyph and not the gutter as well. */
export const DRAG_COL_CLASS = '-ml-2 w-5 shrink-0'

/** The row menu. Reserved for every row of a table that has one, so a row
 *  whose menu is empty keeps its cells on the same edges as the rest. */
export const ACTIONS_COL_CLASS = 'w-8 shrink-0'

/**
 * The columns a row reserves before and after its cells.
 *
 * One list, read by the header, the rows and the skeleton, because the three
 * have to reserve the same columns in the same order or their cells sit on
 * different edges. Deriving it separately three times is what put the header
 * one gutter right of its rows.
 */
export type GutterKey = 'drag' | 'select' | 'expand' | 'actions'

export type Gutters = {
  leading: { key: GutterKey; className: string }[]
  trailing: { key: GutterKey; className: string }[]
}

export function gutters({
  drag,
  select,
  expand,
  actions,
}: Record<GutterKey, boolean>): Gutters {
  return {
    leading: [
      ...(drag ? [{ key: 'drag' as const, className: DRAG_COL_CLASS }] : []),
      ...(select
        ? [{ key: 'select' as const, className: SELECTION_COL_CLASS }]
        : []),
      ...(expand
        ? [{ key: 'expand' as const, className: EXPAND_COL_CLASS }]
        : []),
    ],
    trailing: actions
      ? [{ key: 'actions' as const, className: ACTIONS_COL_CLASS }]
      : [],
  }
}

/** Indent per nesting level, so a child sits under its parent's label. */
export const TREE_INDENT_PX = 20

export type TreeConfig<T> = {
  /**
   * Nesting level, indenting the chevron column and the cells after it. Omit
   * where the nesting
   * is already visible — a tree whose branches are `heading` bands says it
   * with the band, and indenting the rows under one only narrows them.
   */
  depth?: (item: T) => number
  /** Whether the row has children to reveal. Rows without get no chevron. */
  hasChildren: (item: T) => boolean
  /**
   * A branch drawn as a band over its children rather than as a row of its
   * own. It keeps its cells, its chevron and its menu, and drops the drag
   * column, so the rows under it read as belonging to it without an indent.
   *
   * For a branch nothing selects and nothing drags — a storey, a group
   * heading. A folder a click opens stays a row.
   *
   * What it gives up is whichever gutter leads the row: the fixed columns
   * before the elastic one where there are any, so the band's label starts on
   * the marks below it, and the drag handle's where the elastic column already
   * leads.
   */
  heading?: (item: T) => boolean
  /**
   * The keys a row stands for when it is ticked — its own where the row is
   * itself something an action can name, and every row under it, revealed or
   * not.
   *
   * With it, ticking a branch ticks everything under it and the branch reads
   * `indeterminate` while only some of those are ticked. Without it every row
   * is ticked by its own key, which is what a flat table does.
   *
   * Collapsed children have to be reachable here: the rows the table renders
   * are the visible ones, so the answer comes from the caller's own tree.
   */
  selectionKeys?: (item: T) => string[]
}

/**
 * Where a drop lands relative to the row under the pointer.
 *
 * `before` and `after` put the dragged row beside that row — same parent, that
 * position. `into` puts it inside it. Which one is meant is read off where in
 * the row's height the pointer is, so one gesture is never two meanings.
 */
export type DropZone = 'before' | 'after' | 'into'

/**
 * Dragging rows to reorder or to refile them.
 *
 * `onMove` receives the two row keys and the zone: only the caller knows what
 * "beside" and "inside" mean for its own tree, so the table reports the
 * gesture and stays out of the semantics.
 */
export type SortableConfig<T = unknown> = {
  onMove: (activeKey: string, overKey: string, zone: DropZone) => void
  mode?: DragMode
  /**
   * Which rows may be picked up. A row that may not takes no drop beside it
   * unless `dropZones` says otherwise; a `heading` band still takes drops into
   * it — a storey whose stacking is derived takes rooms without being one.
   */
  canDrag?: (item: T) => boolean
  /**
   * Which drops this pair allows.
   *
   * Keys, like `onMove` — the caller owns what a key stands for, and that is
   * what lets a row dragged in from beside the table (under a
   * `TableDragScope`) be judged by the same rules as one of its own.
   *
   * The row under the pointer is banded top to bottom by what comes back, in
   * the order `before, into, after`: three zones split 30/40/30, two at the
   * halfway line, one taking the whole row. An empty list refuses the drop,
   * and nothing is drawn.
   *
   * That is what keeps one gesture from meaning two things: a row that can
   * only be dropped *into* has no edge that inserts beside it, and one that
   * takes no children has no middle that swallows the drag.
   *
   * Leaving it out reads the rule off the rows: a `heading` band takes `into`,
   * a row `canDrag` refuses takes nothing, and every other row takes `before`
   * and `after`. State it here only where a pair needs more than its rows say
   * — a branch that may not land in its own subtree, a kind that fits under
   * one parent and not another.
   */
  dropZones?: (active: string, over: string) => readonly DropZone[]
  /**
   * The row the drop would land *inside*, marked while the drag hovers.
   *
   * An insertion line says where a row lands but not what it lands in, and one
   * line of indent is all that separates "first thing inside this branch" from
   * "next to the branch itself". Marking the enclosing row settles it, and
   * `null` says the drop lands at the top level, where there is nothing to
   * mark.
   *
   * Return `null` for a drop the row itself already marks — a drop *into* the
   * row under the pointer is marked there and needs no second outline.
   */
  dropParentKey?: (
    active: string,
    over: string,
    zone: DropZone,
  ) => string | null
  /**
   * What follows the pointer while a row is dragged.
   *
   * Keyed rather than given the row, because what moves is not always the row
   * the gesture started on — a table that drags a whole ticked selection says
   * so here, and nowhere else would know.
   *
   * Required because the rows hold still during a drag: this is the only
   * thing that shows what is being moved.
   */
  dragPreview: (active: string) => ReactNode
}

/** The row under the pointer and where in it a drop would land. */
export type DropTarget = {
  key: string
  zone: DropZone
  parentKey: string | null
}

// ─── Props ──────────────────────────────────────────────────────────────────

export type DataTableProps<T> = {
  items: T[]
  columns: DataTableColumn<T>[]
  keyExtractor: (item: T, index: number) => string

  isLoading?: boolean
  /** The list could not be fetched. Said above the rows, so a failed refetch
   *  does not blank a table that still holds the last good page. */
  isError?: boolean
  emptyTitle?: string
  emptyDescription?: string
  emptyIcon?: LucideIcon

  toolbarItems?: ToolbarItem[]
  /**
   * The screen's filter state. The toolbar reads whether anything narrows the
   * list and how to widen it again — passed as two separate props, forgetting
   * one silently dropped the reset button, because the toolbar only draws it
   * when both arrive.
   */
  filters?: Pick<ListFilters<FilterSchema>, 'hasActiveFilters' | 'reset'>
  /** Overrides what `filters` says — for a list whose "active" rule differs. */
  hasActiveFilters?: boolean
  /**
   * The row tally beside the toolbar. Renders "12 von 340 Objekten" while a
   * filter narrows the list and "340 Objekte" otherwise.
   */
  count?: ToolbarCount
  /** Overrides `count` with a literal label. */
  countLabel?: string
  toolbarExtra?: ReactNode

  sortState?: SortState
  onSortChange?: (next: SortState) => void

  /** Draws the table in a panel frame. Off for a table filling a pane. */
  card?: boolean
  /**
   * Keeps the columns inside the pane: the flexible one truncates rather than
   * the list scrolling sideways.
   *
   * For a list that is a navigation — a name with a figure or a control beside
   * it — where a column pushed past the edge is a control nobody finds. A list
   * of columns that have to stay readable leaves it off and scrolls.
   */
  fit?: boolean
  /**
   * Lets the reader drag a column's right edge at the head, and double-click it
   * to put the declared width back.
   *
   * Off for a list that is a navigation — a name and a figure or two beside it,
   * a picker in a dialog, a dashboard card — where there is no second column
   * for a width to come out of and nothing a wider one would reveal. On for a
   * list of columns somebody works in.
   */
  resizableColumns?: boolean
  /**
   * Where the dragged column widths are remembered, one key per table: for as
   * long as the page is loaded, a walk through other screens included. A reload
   * starts from the declared widths again. Without it they last only as long as
   * the table is mounted.
   *
   * Not `storageKey`: `MasterDetailLayout` and `SplitPanes` carry one of that
   * name for a width that outlives the visit, and the same word on the same
   * element would say these do too.
   */
  columnWidthKey?: string

  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void

  /**
   * Returning `false` means "not handled" — the table then falls through to
   * its own row action, which is toggling expansion.
   */
  onRowPress?: (item: T) => void | boolean
  selectedRowKey?: string | null
  /**
   * What a row stands for, where that is not the row itself — the same
   * configurator section is a row under every category holding it, and
   * selecting one selects the section, so every one of them reads as selected.
   * Compared against `selectedRowKey`, which the caller states in the same
   * terms. Defaults to the row's own key; the plural of this is
   * `TreeConfig.selectionKeys`, which answers it for a tick box.
   */
  selectionKey?: (item: T) => string
  rowActions?: (item: T) => MenuAction[]

  /**
   * `single` (default) is the pane-opening behaviour driven by
   * `selectedRowKey`. `multi` adds a leading checkbox column and the
   * selection lives in the caller.
   */
  selectionMode?: 'single' | 'multi'
  selectedIds?: Set<string>
  onSelectionChange?: (ids: Set<string>) => void
  /**
   * Shift-click extends the selection from the last clicked row to this one.
   * Off by default: on a table whose rows come and go the anchor is
   * meaningless, and a range nobody asked for is worse than no range.
   */
  rangeSelect?: boolean

  tree?: TreeConfig<T>
  /**
   * Controlled expansion. Supply both to own the state — needed when something
   * other than a click expands a row, e.g. a search that reveals the folders
   * holding its hits. Omit both and the table keeps its own.
   */
  expandedKeys?: Set<string>
  onExpandedChange?: (keys: Set<string>) => void
  /**
   * The branches on the way to the row `selectedRowKey` names — what has to be
   * open for that row to exist.
   *
   * With it, `selectedRowKey` is enough to reach a row: the table opens the
   * branches holding it and then scrolls to it, which is what a link from
   * another screen needs. The row is precisely the one missing from `items`
   * while its branch is shut, so the answer comes from the caller's own tree.
   *
   * It answers about a key rather than a row, which is why it sits beside
   * `selectedRowKey` and not in `TreeConfig` — everything there is a function
   * of a rendered row, which lets a screen keep one frozen config.
   */
  ancestorsOf?: (selectionKey: string) => string[]

  sortable?: SortableConfig<T>
}
